"use client";
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { db } from "../firebase/firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { UserContext } from "../context/UserContext";
import { toast } from "react-toastify";

const socialsList = [
  "github",
  "linkedin",
  "twitter",
  "facebook",
  "instagram",
  "youtube",
  "medium",
  "devto",
  "hashnode",
  "dribbble",
  "behance",
  "stackoverflow",
  "reddit",
  "discord",
  "telegram",
  "whatsapp",
  "portfolio",
  "website",
];

export default function SocialLinksForm() {
  const [formData, setFormData] = useState(
    socialsList.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );
  const [loading, setLoading] = useState(true);
  const { user } = useContext(UserContext);

  useEffect(() => {
    const fetchSocials = async () => {
      if (!user?.id) return;
      try {
        const docRef = doc(db, "users", user.id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.socials) {
            const socialsObj = data.socials.reduce((acc, item) => {
              acc[item.name] = item.url;
              return acc;
            }, {});
            setFormData((prev) => ({ ...prev, ...socialsObj }));
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSocials();
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      toast.error("Please log in first!");
      return;
    }

    const socials = socialsList.map((name) => ({
      name,
      url: formData[name],
    }));

    try {
      await setDoc(doc(db, "users", user.id), { socials }, { merge: true });
      toast.success("✅ Social links updated successfully!");
    } catch (err) {
      console.error("Error saving socials:", err);
      toast.error("❌ Failed to save links!");
    }
  };

  if (loading)
    return (
      <Container>
        <Card>
          <Title>Loading your socials...</Title>
        </Card>
      </Container>
    );

  return (
    <Container>
      <Card>
        <Title>🌐 Add Your Social Links</Title>
        <Form onSubmit={handleSubmit}>
          <Grid>
            {socialsList.map((social, i) => (
              <FieldGroup key={i}>
                <Label>
                  {social.charAt(0).toUpperCase() + social.slice(1)}
                </Label>
                <Input
                  type="url"
                  name={social}
                  value={formData[social]}
                  onChange={handleChange}
                  placeholder={`Enter ${social} URL`}
                />
              </FieldGroup>
            ))}
          </Grid>
          <Button type="submit">Save Social Links</Button>
        </Form>
      </Card>
    </Container>
  );
}

/* ------------------ Styled Components ------------------ */

const Container = styled.div`
  background: radial-gradient(circle at top left, #0b0617, #000);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 800px;
  color: #fff;
  box-shadow: 0 0 40px rgba(155, 92, 255, 0.2);

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 26px;
  margin-bottom: 30px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px 22px;
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  color: #bbb;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  transition: all 0.3s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border: 1px solid rgba(155, 92, 255, 0.5);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.2);
  }

  &::placeholder {
    color: #777;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 0;
  margin-top: 20px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.3);
  }
`;
